import { useEffect, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Alert } from '@/common/components/Alert/Alert';
import { BodyText } from '@/common/components/BodyText/BodyText';
import { Button } from '@/common/components/Button/Button';
import { Form } from '@/common/components/Form/Form';
import { LoadingPlaceholder } from '@/common/components/LoadingPlaceholder/LoadingPlaceholder';
import { PageHeader } from '@/common/components/PageHeader/PageHeader';
import { Stack } from '@/common/components/Stack/Stack';
import { TextAction } from '@/common/components/TextAction/TextAction';
import { Translate } from '@/common/components/Translate/Translate';
import { TMarket } from '@/common/constants/markets';
import {
  CreatePasswordFormFields,
  useCreatePasswordForm,
} from '@/common/containers/Authentication/components/CreatePasswordForm/CreatePasswordForm';
import { ICreatePasswordFormData } from '@/common/containers/Authentication/components/CreatePasswordForm/CreatePasswordForm.schema';
import { LanguageSwitcher } from '@/common/containers/Authentication/components/LanguageSwitcher/LanguageSwitcher';
import { AnalyticsService } from '@/common/integrations/analytics/analytics.service';
import { AuthService } from '@/common/integrations/menhir/employee/sessions/sessions.service';
import { ISignInResponse } from '@/common/integrations/menhir/employee/sessions/sessions.types';
import { AuthFormLayout } from '@/common/layouts/AuthLayout/AuthLayout';
import { logException } from '@/common/utils/exceptionLogger';
import { notify } from '@/common/utils/toast/toast';
import { usePageViewTracking } from '@/web/hooks/usePageViewTracking';
import { useLocalSearchParams } from 'expo-router';

interface ISignupFormProps {
  token: string;
  onSubmit: (response: ISignInResponse) => void;
  isSubmitting?: boolean;
}

export const SignupForm = ({
  token,
  onSubmit,
  isSubmitting,
}: ISignupFormProps) => {
  const { t } = useTranslation();
  usePageViewTracking('create_password_page');
  const queryParams = useLocalSearchParams<{
    market: TMarket;
  }>();
  const [validating, setValidating] = useState(!isSubmitting);
  const requestedRef = useRef(false);
  const { methods } = useCreatePasswordForm(token);
  const [error, setError] = useState(false);
  const market = queryParams.market;

  useEffect(() => {
    if (requestedRef.current) return;
    requestedRef.current = true;

    (async () => {
      if (isSubmitting) return;
      setValidating(true);
      try {
        await AuthService.validateInvitation(token);
      } catch (error: any) {
        if (error?.errorCode === 'not_found') {
          setError(true);
        }
      } finally {
        setValidating(false);
      }
    })();
  }, [isSubmitting, token, t]);

  const handleSubmit = async (form: ICreatePasswordFormData) => {
    const { token, password } = form;
    AnalyticsService.trackCustomEvent('create_password_button_clicked');
    try {
      const response = await AuthService.signUp(token, password);
      onSubmit(response);
      notify({
        type: 'success',
        message: t('signup.created_modal.title'),
      });
    } catch (e: any) {
      if (e?.errorCode === 'not_found') {
        setError(true);
      }
    }
  };

  if (validating && !isSubmitting) {
    return <LoadingPlaceholder />;
  }

  if (!market) {
    logException(
      'The url for the user signup does not contain the market query parameter.'
    );
  }

  return (
    <AuthFormLayout
      footer={
        <Button
          onPress={methods.handleSubmit(handleSubmit)}
          loading={methods.formState.isSubmitting}
        >
          {t('authentication.signup.form.button')}
        </Button>
      }
    >
      <Stack gap="xl">
        <PageHeader
          title={t('authentication.signup.form.title')}
          actions={<LanguageSwitcher market={market} />}
        />
        {error && (
          <Alert type="error">
            <Translate i18nKey="signup.invalid_link_error" />
          </Alert>
        )}
        <FormProvider {...methods}>
          <Form onSubmit={methods.handleSubmit(handleSubmit)}>
            <Stack gap="md">
              <CreatePasswordFormFields />
              <BodyText data-test-id={`terms-of-use-${market}`}>
                <Translate
                  i18nKey="authentication.create_password.terms_of_use_and_privacy_policy"
                  components={{
                    termsOfUse: (
                      <TextAction
                        href={t(
                          `general.links.terms_of_use.${market ? market : 'pt'}.url`
                        )}
                        hrefAttrs={{
                          target: '_blank',
                          rel: 'noreferrer',
                        }}
                      />
                    ),
                    privacyPolicy: (
                      <TextAction
                        href={t(
                          `general.links.privacy_policy.${market ? market : 'pt'}.url`
                        )}
                        hrefAttrs={{
                          target: '_blank',
                          rel: 'noreferrer',
                        }}
                      />
                    ),
                  }}
                />
              </BodyText>
            </Stack>
          </Form>
        </FormProvider>
      </Stack>
    </AuthFormLayout>
  );
};
